import React, {useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSearchParams} from 'react-router-dom';

// @mui
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';

// icons
import CloseIcon from '@mui/icons-material/Close';

// API Hooks
import {useExternalCompanyDetails} from 'packages/common/hooks/api/widgets/company/Company';
import {useExternalGiftVoucherTypes, useExternalCatalogCategories} from 'packages/common/hooks/api/widgets/giftVouchers/GiftVouchers';

// hooks
import {IframeResizeProvider, useIframeResize} from '../../common/contexts/IframeResizeContext';
import {getErrorInfo, createStripeNotConfiguredError} from '../../common/hooks/useWidgetError';

// components
import {ConfigurationError} from '../../common/components/ConfigurationError';
import WidgetErrorBoundary from '../../common/components/WidgetErrorBoundary';
import GiftVoucherGrid from '../components/GiftVoucherGrid';
import NoVouchersDisplay from '../components/NoVoucherDisplay';
import GiftVoucherFilters from '../components/GiftVoucherFilters';

// utils
import {isInIframe} from '../../../utils/iframeUtils';
import {sendCloseMessage} from '../../common/utils/iframeMessaging';
import {sortVouchers, filterVouchersByPrice, calculatePriceRange} from '../utils/voucherSorting';

// styled components
import {
  LoadingContainer,
  WidgetContainer,
  WidgetHeader,
  ContentContainer,
  FixedHeaderContainer,
  HeaderSpacerBox,
  ScrollableContentBox,
} from '../../common/components/styled/WidgetViewStyles';

export default function GiftVoucherView({
  mode = 'fixed',
  companyId: propCompanyId = null,
  apiKey: propApiKey = null,
  instanceId = 'default-instance',
}) {
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');

  // Parse params and props
  const embedded = isInIframe();
  const displayMode = (urlMode || mode).toLowerCase().trim();
  const companyId = propCompanyId || searchParams.get('companyId') || null;
  const apiKey = propApiKey || searchParams.get('apiKey') || null;

  const urlInstanceId = searchParams.get('instanceId');
  const effectiveInstanceId = urlInstanceId || instanceId;
  const customColor = searchParams.get('customColor');

  return (
    <IframeResizeProvider
      mode={displayMode}
      instanceId={effectiveInstanceId}
      minHeight={500}
      maxHeight={displayMode === 'floating' ? '75vh' : 10000}
      debounceDelay={200}
    >
      <WidgetErrorBoundary widgetType="giftvouchers">
        <GiftVoucherContent
          mode={mode}
          displayMode={displayMode}
          embedded={embedded}
          companyId={companyId}
          apiKey={apiKey}
          instanceId={effectiveInstanceId}
          customColor={customColor}
        />
      </WidgetErrorBoundary>
    </IframeResizeProvider>
  );
}

function GiftVoucherContent({
  mode,
  displayMode,
  embedded,
  companyId: propCompanyId,
  apiKey,
  instanceId,
  customColor,
}) {
  const {containerRef, forceResize, updateHeight} = useIframeResize();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || propCompanyId;

  // API calls - Get data and errors from hooks
  const {
    companyDetails,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyError,
  } = useExternalCompanyDetails(companyId, apiKey);

  const {
    giftVoucherTypes,
    isLoading: isVoucherTypesLoading,
    isError: isVoucherTypesError,
    error: voucherTypesError,
  } = useExternalGiftVoucherTypes(apiKey);

  const {
    categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useExternalCatalogCategories(apiKey);

  // Determine which error to display (priority: company > vouchers > categories > stripe)
  const currentError = (() => {
    // First check API errors
    if (isCompanyError) return companyError;
    if (isVoucherTypesError) return voucherTypesError;
    if (isCategoriesError) return categoriesError;

    // Then check Stripe configuration (only if company details loaded successfully)
    if (companyDetails && !companyDetails?.stripeAccountId) {
      return createStripeNotConfiguredError('giftvouchers');
    }

    return null;
  })();

  // Theme and colors
  const primaryColor = customColor ||
                     companyDetails?.branding?.primaryColor ||
                     companyDetails?.primaryColor ||
                     null;

  // Filter state management
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [sortBy, setSortBy] = React.useState('default');

  // Price range calculation
  const priceRangeData = useMemo(() => {
    const result = calculatePriceRange(giftVoucherTypes);
    return result;
  }, [giftVoucherTypes]);

  const [priceRange, setPriceRange] = React.useState([priceRangeData.min, priceRangeData.max]);

  // Update price range when vouchers change
  useEffect(() => {
    setPriceRange([priceRangeData.min, priceRangeData.max]);
  }, [priceRangeData.min, priceRangeData.max]);

  // Handle close widget
  const handleCloseWidget = useCallback(() => {
    if (embedded) {
      sendCloseMessage(instanceId);
    }
  }, [embedded, instanceId]);

  // Filter and sort vouchers - NOUVELLE LOGIQUE AVEC FEATURED EN PRIORITÉ
  const filteredVouchers = useMemo(() => {
    if (!giftVoucherTypes) {
      return [];
    }

    // 1. Filtrer par disponibilité
    let filtered = giftVoucherTypes.filter((voucher) => {
      // Nouveau format
      if (voucher.isAvailable !== undefined) {
        return voucher.isAvailable;
      }
      // Ancien format (compatibilité)
      return voucher.typeStatus;
    });

    // 2. Filtrer par catégories sélectionnées
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((voucher) => {
        return selectedCategories.some((categoryId) =>
          voucher.categories?.includes(categoryId),
        );
      });
    }

    // 3. Filtrer par prix
    filtered = filterVouchersByPrice(filtered, priceRange);

    // 4. Trier avec featured en priorité
    filtered = sortVouchers(filtered, sortBy);

    return filtered;
  }, [giftVoucherTypes, selectedCategories, priceRange, sortBy]);

  // Handle filter reset
  const handleFiltersReset = useCallback(() => {
    setSelectedCategories([]);
    setPriceRange([priceRangeData.min, priceRangeData.max]);
    setSortBy('default');
  }, [priceRangeData.min, priceRangeData.max]);

  // Update iframe height when content changes
  useEffect(() => {
    if (displayMode === 'floating' && !isCompanyLoading && !isVoucherTypesLoading && !currentError) {
      const timer = setTimeout(() => {
        updateHeight();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [
    displayMode,
    updateHeight,
    isCompanyLoading,
    isVoucherTypesLoading,
    currentError,
    filteredVouchers.length,
    selectedCategories.length,
    priceRange,
    sortBy,
  ]);

  // Loading state
  if (isCompanyLoading || isVoucherTypesLoading) {
    return (
      <WidgetContainer embedded={embedded} ref={containerRef} widgetType="giftvouchers">
        <LoadingContainer>
          <CircularProgress size={40} sx={{mb: 2}} color="primary" />
          <Typography variant="body2">Chargement des bons cadeaux...</Typography>
        </LoadingContainer>
      </WidgetContainer>
    );
  }

  // Unified Error state - Use getErrorInfo utility function
  if (currentError) {
    const errorInfo = getErrorInfo(currentError);
    return (
      <WidgetContainer embedded={embedded} ref={containerRef}>
        <WidgetHeader variant="error">
          <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
            {errorInfo.title}
          </Typography>
          {displayMode === 'floating' && (
            <IconButton size="small" onClick={handleCloseWidget} sx={{color: 'inherit'}}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </WidgetHeader>
        <ContentContainer>
          <ConfigurationError
            title={errorInfo.title}
            message={errorInfo.message}
            details={errorInfo.details}
            onClose={handleCloseWidget}
            showCloseButton={embedded && displayMode === 'floating'}
          />
        </ContentContainer>
      </WidgetContainer>
    );
  }

  // Success state - render the widget content
  return (
    <Box sx={{
      ...(!embedded && {
        paddingTop: 4,
      }),
    }}>
      <WidgetContainer
        embedded={embedded}
        displayMode={displayMode}
        ref={containerRef}
        maxWidth="100%"
        maxHeight="100%"
        widgetType="giftvouchers"
      >
        {/* Header for embedded mode */}
        {embedded && displayMode !== 'fixed' && (
          <>
            <FixedHeaderContainer embedded={embedded} displayMode={displayMode}>
              <WidgetHeader primaryColor={primaryColor} variant="default">
                <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
                  Bons Cadeaux {companyDetails?.name || ''}
                </Typography>
                {displayMode === 'floating' && (
                  <IconButton size="small" onClick={handleCloseWidget} sx={{color: 'inherit'}}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </WidgetHeader>
            </FixedHeaderContainer>
            <HeaderSpacerBox hasSteps={false} embedded={embedded} />
          </>
        )}

        {/* Scrollable content */}
        <ScrollableContentBox hasFooter={false} embedded={embedded}>
          <Fade in timeout={300} onEntered={forceResize}>
            <Box>
              {/* Filters - only show when we have valid data and no errors */}
              <GiftVoucherFilters
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                maxPrice={priceRangeData.max}
                sortBy={sortBy}
                setSortBy={setSortBy}
                categories={categories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                isCategoriesLoading={isCategoriesLoading}
                primaryColor={primaryColor}
                embedded={embedded}
                displayMode={displayMode}
                onReset={handleFiltersReset}
              />

              {/* Gift vouchers display */}
              {filteredVouchers.length === 0 ? (
                <NoVouchersDisplay />
              ) : (
                <GiftVoucherGrid
                  primaryColor={primaryColor}
                  voucherTypes={filteredVouchers}
                  companyDetails={companyDetails}
                  categories={categories}
                />
              )}
            </Box>
          </Fade>
        </ScrollableContentBox>
      </WidgetContainer>
    </Box>
  );
}

GiftVoucherView.propTypes = {
  mode: PropTypes.oneOf(['fixed', 'floating']),
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  instanceId: PropTypes.string,
};

GiftVoucherContent.propTypes = {
  mode: PropTypes.string.isRequired,
  displayMode: PropTypes.string.isRequired,
  embedded: PropTypes.bool.isRequired,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  instanceId: PropTypes.string.isRequired,
  customColor: PropTypes.string,
};
